import { useEffect, type RefObject } from "react";

/** Пауза без движения по холсту — chrome чуть уходит на задний план. */
const CHROME_IDLE_MS = 2400;

const CHROME_KEEP_ALIVE =
  ".wb-floating-tools, .wb-floating-toolbar, .wb-board-floating-toolbar, .wb-zoom-controls, .wb-tool-rail, .wb-board-status, .wb-selection-toolbar, .wb-tool-options-popover, .wb-minimap, .wb-board-frames, .wb-shape-flyout, .wb-widgets-flyout, .wb-board-call-toggle";

/**
 * Тихий idle-chrome: когда курсор на холсте и не двигается —
 * рейл/зум/миникарта приглушаются. Hover по chrome возвращает яркость.
 */
export function useBoardChromeQuiet(
  mainRef: RefObject<HTMLElement | null>,
  enabled = true
) {
  useEffect(() => {
    if (!enabled) return;
    const main = mainRef.current;
    if (!main) return;

    let timer = 0;

    const clearQuiet = () => {
      main.classList.remove("is-chrome-quiet");
    };

    const armQuiet = () => {
      clearQuiet();
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        if (main.querySelector(".wb-tool-options-popover")) return;
        if (main.querySelector(".wb-board-viewport.is-navigating")) return;
        if (main.querySelector(".wb-board-viewport.is-selection-dragging")) return;
        main.classList.add("is-chrome-quiet");
      }, CHROME_IDLE_MS);
    };

    const onPointer = (ev: PointerEvent) => {
      const t = ev.target;
      if (t instanceof Element && t.closest(CHROME_KEEP_ALIVE)) {
        clearQuiet();
        window.clearTimeout(timer);
        return;
      }
      armQuiet();
    };

    const onFocusIn = (ev: FocusEvent) => {
      const t = ev.target;
      if (t instanceof Element && t.closest(CHROME_KEEP_ALIVE)) {
        clearQuiet();
        window.clearTimeout(timer);
      }
    };

    const onWake = () => {
      clearQuiet();
      window.clearTimeout(timer);
    };

    main.addEventListener("pointermove", onPointer, { passive: true });
    main.addEventListener("pointerdown", onPointer, { passive: true });
    main.addEventListener("focusin", onFocusIn);
    window.addEventListener("wb:user-camera", onWake);
    window.addEventListener("wb:selection-drag", onWake);

    armQuiet();

    return () => {
      window.clearTimeout(timer);
      clearQuiet();
      main.removeEventListener("pointermove", onPointer);
      main.removeEventListener("pointerdown", onPointer);
      main.removeEventListener("focusin", onFocusIn);
      window.removeEventListener("wb:user-camera", onWake);
      window.removeEventListener("wb:selection-drag", onWake);
    };
  }, [mainRef, enabled]);
}
